import { Button } from '@breetometer/components';
import { CloseModal } from 'BreetAssets';
import { modalMaxHeightConfig, modalMaxWidthConfig } from 'BreetConfig';
import { type ModalProps } from 'BreetHelpers';
import { closeModal, selectModals, useAppDispatch, useAppSelector } from 'BreetRedux';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const Modal: FC<PropsWithChildren<ModalProps>> = ({
	modalId,
	manualClose,
	onClose,
	children,
	wrapperClassName,
	dialogMessage,
	variant,
	hideCloseButton,
	fullScreen,
}) => {
	const modalContentRef = useRef<HTMLDivElement>(null);

	const dispatch = useAppDispatch();

	const handleContainerClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (manualClose) return;
			if (e.detail === 0) return;
			if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
				if (!document.contains(e.target as Node)) return;

				const clickedInsideIgnoredPortal = (e.target as Element).closest('[data-modal-ignore="true"]');

				if (clickedInsideIgnoredPortal) return;

				if (onClose) onClose(false);
				dispatch(closeModal(modalId));
			}
		},
		[dispatch, modalId, manualClose, onClose]
	);

	const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

	const modals = useAppSelector(selectModals);

	const currentModal = !!modals.find((modal) => modalId !== 'initialModal' && modal.id === modalId);

	useEffect(() => {
		setPortalRoot(document.getElementById('bt_modal_root'));
	}, []);

	useEffect(() => {
		if (dialogMessage && modalId === 'loadingDialog') {
			const timer = setTimeout(() => {
				const element = document.getElementById('loading-dialog-message');
				if (element) {
					element.textContent = dialogMessage;
				}
			}, 0);
			return () => clearTimeout(timer);
		}
	}, [dialogMessage, modalId]);

	const handleModalVisibility = useCallback(() => {
		if (manualClose) return;
		if (onClose) onClose(false);
		dispatch(closeModal(modalId));
	}, [dispatch, modalId, manualClose, onClose]);

	if (!portalRoot || !currentModal) {
		return null;
	}

	const isDialogMessage = modalId === 'loadingDialog';
	const dialogMessageClass = isDialogMessage ? ' dialogMessage' : '';
	const stateClass = ' bt_modal_state--open';
	const variantClass = variant === 'right-sheet' ? ' right-sheet' : '';
	const customClass = wrapperClassName ? ` ${wrapperClassName}` : '';
	const fullScreenClass = fullScreen ? ' fullScreen' : '';

	const className = `${stateClass}${variantClass}${customClass}${dialogMessageClass}${fullScreenClass}`;

	const modalMaxWidth = modalMaxWidthConfig[modalId];
	const modalMaxHeight = modalMaxHeightConfig[modalId];
	const modalStyle = modalMaxWidth ? { maxWidth: modalMaxWidth, maxHeight: modalMaxHeight } : undefined;

	return createPortal(
		<>
			<div className={`bt_modal_mask${stateClass}`} />

			<Button
				variant='native'
				className={`button-cancel-transform bt_modal_container${className}`}
				data-testid='modalContainer'
				onClick={handleContainerClick}
			>
				<div
					className='bt_modal_content'
					style={modalStyle}
					ref={modalContentRef}
				>
					{!hideCloseButton && (
						<div className='bt_modal_content_header'>
							<CloseModal
								aria-label='close modal'
								focusable
								onClick={handleModalVisibility}
								id={`closeBtnRef-${modalId}`}
								className={`bt_modal_close_btn${customClass} svg-dark cursor_pointer`}
							/>
						</div>
					)}
					<div className='bt_modal_content_body'>{children}</div>
				</div>
			</Button>
		</>,
		portalRoot
	);
};

export default Modal;
